import { observer } from 'mobx-react';
import { FC, useState } from 'react';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { Card } from '@monorepo/base/src/components/card/card';
import { Dropdown } from '@monorepo/base/src/components/dropdown/dropdown';
import { Textarea } from '@monorepo/base/src/components/form/textarea/textarea';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import { useList } from '../../../modules/hooks/tools/use-list';
import { useStateBetweenPages } from '../../../modules/hooks/tools/use-state-between-pages';
import { BidderCampaignTableModel } from '../../../modules/models/bidder-campaign/bidder-camapign.model';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { BidderApi, UpdateMaxLossClicksDomainType, UpdateMaxLossClicksOperation } from '../../../modules/apis/bidder.api';
import styles from './update-max-loss-clicks.module.scss';

function parseCampaignIdFromLabel(label: string): number | null {
	const idPrefix = label.match(/^(\d+)\s*-\s*/);
	if (idPrefix) {
		return Number(idPrefix[1]);
	}
	const inParens = label.match(/\((\d+)\)\s*$/);
	return inParens ? Number(inParens[1]) : null;
}

const OPERATION_ROWS: { value: UpdateMaxLossClicksOperation; label: string }[] = [
	{ value: 'updateMaxLoss', label: 'Update Max Loss' },
	{ value: 'updateMaxClicks', label: 'Update Max Clicks' },
	{ value: 'both', label: 'Both' },
];

const OPERATION_OPTIONS = OPERATION_ROWS.map(r => r.label);

function labelForOperation(o: UpdateMaxLossClicksOperation): string {
	return OPERATION_ROWS.find(r => r.value === o)?.label ?? 'Update Max Loss';
}

function operationFromLabel(label: string | undefined): UpdateMaxLossClicksOperation | undefined {
	if (!label) {
		return undefined;
	}
	return OPERATION_ROWS.find(r => r.label === label)?.value;
}

const DOMAIN_SCOPE_ROWS: { value: UpdateMaxLossClicksDomainType; label: string }[] = [
	{ value: 'all', label: 'All' },
	{ value: 'include', label: 'Include — only domains listed below' },
	{ value: 'exclude', label: 'Exclude — not assigned to the domain list below' },
];

const DOMAIN_SCOPE_LABELS = DOMAIN_SCOPE_ROWS.map(r => r.label);

function labelForDomainType(t: UpdateMaxLossClicksDomainType): string {
	return DOMAIN_SCOPE_ROWS.find(r => r.value === t)?.label ?? 'All';
}

function domainTypeFromLabel(label: string | undefined): UpdateMaxLossClicksDomainType | undefined {
	if (!label) {
		return undefined;
	}
	return DOMAIN_SCOPE_ROWS.find(r => r.label === label)?.value;
}

function firstFromSet(val: Set<string> | undefined): string | undefined {
	if (!val || val.size === 0) {
		return undefined;
	}
	return [...val][0];
}

interface IUpdateMaxLossClicksFormProps {
	campaigns: BidderCampaignTableModel[];
	isCampaignsLoading: boolean;
	onSubmitSuccess: () => void;
}

const UpdateMaxLossClicksForm: FC<IUpdateMaxLossClicksFormProps> = ({ campaigns, isCampaignsLoading, onSubmitSuccess }) => {
	const { dispatchLog } = useLogs();
	const [campaignSelection, setCampaignSelection] = useState<string | null>(null);
	const [operation, setOperation] = useState<UpdateMaxLossClicksOperation>('updateMaxLoss');
	const [domainType, setDomainType] = useState<UpdateMaxLossClicksDomainType>('all');
	const [domainsList, setDomainsList] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const campaignDropdownOptions = campaigns.map(c => `${c.id} - ${c.name}`);

	const onSubmit = async () => {
		if (campaignSelection == null || campaignSelection === '') {
			dispatchLog({
				msg: 'Please select a campaign',
				type: DispatchLogsTypes.Error,
			});
			return;
		}
		const campaignId = parseCampaignIdFromLabel(campaignSelection);
		if (campaignId == null || !Number.isFinite(campaignId)) {
			dispatchLog({
				msg: 'Invalid campaign selection',
				type: DispatchLogsTypes.Error,
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const res = await BidderApi.updateMaxLossClicks({
				update_max_loss_clicks: {
					campaign_id: campaignId,
					operation,
					type: domainType,
					domains_list: domainType === 'all' ? '' : domainsList.trim(),
				},
			});
			const ok = res.success === true || res.status === 'success';
			if (ok) {
				dispatchLog({
					msg: `Update succeeded for campaign ${res.campaign_id}`,
					type: DispatchLogsTypes.Success,
				});
				onSubmitSuccess();
			} else {
				dispatchLog({
					msg: `Unexpected response: ${JSON.stringify(res)}`,
					type: DispatchLogsTypes.Error,
				});
			}
		} catch (e: unknown) {
			const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Request failed';
			dispatchLog({
				msg,
				type: DispatchLogsTypes.Error,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.formShell}>
			<div className={styles.formDropdowns}>
				<Dropdown
					label="Campaign"
					placeholder="Select campaign"
					required
					autocomplete
					autocompletePlaceholder="Search by name or campaign ID"
					isLoading={isCampaignsLoading}
					disabled={isCampaignsLoading || campaignDropdownOptions.length === 0}
					options={campaignDropdownOptions}
					defaultOption={campaignSelection ?? undefined}
					onSelect={val => setCampaignSelection(firstFromSet(val) ?? null)}
					isCloseIcon={true}
				/>
				<Dropdown
					label="Operation"
					options={OPERATION_OPTIONS}
					defaultOption={labelForOperation(operation)}
					onSelect={val => {
						const v = operationFromLabel(firstFromSet(val));
						if (v) {
							setOperation(v);
						}
					}}
					isCloseIcon={false}
				/>
				<Dropdown
					label="Domain scope"
					options={DOMAIN_SCOPE_LABELS}
					defaultOption={labelForDomainType(domainType)}
					onSelect={val => {
						const v = domainTypeFromLabel(firstFromSet(val));
						if (v) {
							setDomainType(v);
						}
					}}
					isCloseIcon={false}
				/>
			</div>
			<div className={styles.formColumn}>
				{domainType !== 'all' ? (
					<Textarea
						placeholder="Domains (comma-separated)"
						value={domainsList}
						onValue={setDomainsList}
						rows={6}
						requiredAsterisk={false}
					/>
				) : null}
				<PrimaryButton
					className={styles.submitButton}
					onClick={onSubmit}
					disabled={isSubmitting || isCampaignsLoading || campaignSelection == null}>
					Submit
				</PrimaryButton>
			</div>
		</div>
	);
};

export const BidderUpdateMaxLossClicksPage = observer(() => {
	const {
		bidderStore: { campaignsList },
	} = useStores<IAdminxStore>();
	const stateBetweenPages = useStateBetweenPages(() => campaignsList.getList().length !== 0);
	useList<BidderCampaignTableModel>(campaignsList, undefined, stateBetweenPages);

	const [formKey, setFormKey] = useState(0);
	const campaigns = campaignsList.getList();
	const isCampaignsLoading = campaignsList.getIsLoading();

	return (
		<Page>
			<div className={styles.pageShell}>
				<div className={styles.pageHeader}>
					<Pagebar>
						<PagebarTitle>Max loss / max clicks</PagebarTitle>
					</Pagebar>
				</div>
				<Card className={styles.pageCard}>
					<UpdateMaxLossClicksForm
						key={formKey}
						campaigns={campaigns}
						isCampaignsLoading={isCampaignsLoading}
						onSubmitSuccess={() => setFormKey(k => k + 1)}
					/>
				</Card>
			</div>
		</Page>
	);
});
